import React, { useCallback, useMemo, useState } from 'react'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import Divider from 'LuxKit/Divider'
import LineEnvelopeIcon from 'LuxKit/Icons/line/LineEnvelopeIcon'
import SolidGoogleIcon from 'LuxKit/Icons/solid/SolidGoogleIcon'
import LineLockIcon from 'LuxKit/Icons/line/LineLockIcon'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Group from 'components/utils/Group'
import AccountAccessLegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import { AccountAccessSurface } from 'components/Account/AccountAccess/AccountAccessModalWrapper'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { reportClientError } from 'services/errorReportingService'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE } from 'components/App/Routes/routeConstants'
import { isOidcProvider, isOidcProviderConfigured, OidcProvider } from 'businessTraveller/auth/oidc/providers'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useWhiteLabelAppExternalLink from 'hooks/useWhiteLabelAppExternalLink'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'

// Loaded on click so oidc-client-ts (~131 KB) stays out of the always-loaded login modal bundle
const loadOidcSignIn = () => import('businessTraveller/auth/oidc/signIn')

interface ProviderOption {
  provider: OidcProvider
  label: string
  icon: React.ReactElement
  kind: 'primary' | 'secondary'
}

const PROVIDER_OPTIONS: Array<ProviderOption> = [
  { provider: 'password', label: 'Sign in with email/password', icon: <LineEnvelopeIcon />, kind: 'primary' },
  { provider: 'google', label: 'Sign in with Google', icon: <SolidGoogleIcon />, kind: 'secondary' },
  { provider: 'sso', label: 'Sign in with SSO', icon: <LineLockIcon />, kind: 'secondary' },
]

interface Props {
  variant?: AccountAccessSurface;
}

// Each option is its own svc-auth client, so the button pressed decides the
// client_id. Chrome-free so it drops into either the modal or the login page.
function BusinessTravellerLoginV2Form({ variant = 'modal' }: Props) {
  const [redirectingProvider, setRedirectingProvider] = useState<OidcProvider | undefined>()

  const isPage = variant === 'page'

  const modalContext = useModalElementContext()
  const whiteLabelApp = useWhiteLabelAppConfig()

  const goToBusinessSignup = useWhiteLabelAppExternalLink(BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE)
  const onSignUpClick = useCallback(() => {
    modalContext.resolve()
    goToBusinessSignup()
  }, [goToBusinessSignup, modalContext])

  // Provider rides on a data attribute so the handler reference stays stable.
  const onSignInClick = useCallback(async(event: React.MouseEvent<HTMLElement>) => {
    const provider = event.currentTarget.dataset.provider
    if (!isOidcProvider(provider)) {
      return
    }

    setRedirectingProvider(provider)

    try {
      const { oidcSignIn } = await loadOidcSignIn()
      const outcome = await oidcSignIn(provider, whiteLabelApp)

      // when the outcome is app, a native sheet is open, so the page can't be tapped until it closes;
      // a dismissed sheet leaves the page as it was, with the button usable.
      if (outcome.kind === 'app') {
        setRedirectingProvider(undefined)
      }
    } catch(error) {
      setRedirectingProvider(undefined)
      reportClientError(error, { context: 'oidcSignIn', provider }).catch(() => undefined)
      showSnackbar('Could not start sign-in. Please try again.', 'critical')
    }
  }, [whiteLabelApp])

  const availableOptions = useMemo(() => PROVIDER_OPTIONS.filter(option => isOidcProviderConfigured(option.provider)), [])

  return (
    <Group direction="vertical" gap={24} fullWidth data-testid="business-traveller-login-v2-form">
      {!isPage && <Group direction="horizontal" horizontalAlign="center"><LogoIcon height={40} width={244} /></Group>}

      <Group direction="vertical" gap={4}>
        <Heading variant={isPage ? 'heading2' : 'heading4'} align={isPage ? 'start' : 'center'}>
          Travelling for work has never been easier
        </Heading>
        <BodyText variant="medium" align={isPage ? 'start' : 'center'} colour="neutral-two">
          Manage your business bookings and expenses – all in one place.
        </BodyText>
      </Group>

      {!availableOptions.length && (
        <BodyText variant="medium" align={isPage ? 'start' : 'center'} colour="critical">
          Sign-in is temporarily unavailable. Please try again later.
        </BodyText>
      )}

      <Group direction="vertical" gap={12} fullWidth>
        {availableOptions.map(({ provider, label, icon, kind }) => (
          <TextButton
            key={provider}
            kind={kind}
            size="large"
            fit="full-width"
            startIcon={icon}
            disabled={redirectingProvider === provider}
            data-provider={provider}
            data-testid={`business-traveller-login-v2-${provider}`}
            onClick={onSignInClick}
          >
            {label}
          </TextButton>
        ))}
      </Group>

      {/* The login page carries its own "Register your business" prompt. */}
      {!isPage && <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
        <Divider kind="primary" style={{ flex: 1 }} />
        <Caption variant="medium" colour="neutral-three">New to Business Traveller?</Caption>
        <Divider kind="primary" style={{ flex: 1 }} />
      </Group>}

      {!isPage && <TextButton kind="secondary" size="large" fit="full-width" data-testid="business-traveller-login-v2-signup" onClick={onSignUpClick}>
        Sign up
      </TextButton>}

      <AccountAccessLegalText />
    </Group>
  )
}

export default BusinessTravellerLoginV2Form
